<template>
  <div class="micro-hitl-demo">
    <section class="embed-purpose" aria-label="Micro HITL purpose">
      <h2 class="embed-purpose__title">Floating widget on top of your page</h2>
      <p class="embed-purpose__text">
        The micro launcher adds a small control that sits above your existing UI—typical for help or chat entry on
        dashboards and line-of-business apps without reserving a full column. The demo below includes a simple
        “page” so you can see the widget anchored to the bottom-right corner.
      </p>
    </section>
    <div class="micro-hitl-stage">
      <div class="micro-hitl-stage__mock-page">
        <header class="micro-hitl-stage__mock-header">Example host application</header>
        <p class="micro-hitl-stage__mock-body">
          Contacts below use
          <code class="micro-hitl-stage__code">HITLAgentUI.emit</code>
          with
          <code class="micro-hitl-stage__code">{ message: 'HITL::…', params }</code>
          (same envelope as the full iframe bridge). Launcher lifecycle events
          such as
          <code class="micro-hitl-stage__code">APP_READY</code>
          arrive via
          <code class="micro-hitl-stage__code">HITLAgentUI.onEvent('APP_READY', cb)</code>
          as
          <code class="micro-hitl-stage__code">{ type, content }</code>.
        </p>

        <section class="micro-hitl-jwt-start" aria-label="Start HITL with JWT">
          <div class="micro-hitl-jwt-start__toolbar">
            <button
              type="button"
              class="micro-hitl-jwt-start__toggle"
              :aria-expanded="authSectionExpanded"
              aria-controls="micro-hitl-auth-panel"
              @click="authSectionExpanded = !authSectionExpanded"
            >
              <span
                class="micro-hitl-jwt-start__chevron"
                :class="{ 'micro-hitl-jwt-start__chevron--open': authSectionExpanded }"
                aria-hidden="true"
              />
              <span class="micro-hitl-jwt-start__toolbar-title">Auth</span>
            </button>
            <p class="micro-hitl-jwt-start__toolbar-status" role="status">
              {{ orTokenStatusLine }}
            </p>
            <div class="micro-hitl-jwt-start__toolbar-actions">
              <button
                type="button"
                class="micro-hitl-jwt-start__btn"
                :disabled="orLogoutInProgress"
                @click="logoutOrSession"
              >
                {{ orLogoutInProgress ? 'Signing out…' : 'Log out' }}
              </button>
              <button
                type="button"
                class="micro-hitl-jwt-start__btn micro-hitl-jwt-start__btn--primary"
                :disabled="!canStartMicroHitl"
                @click="startMicroHitlEmbed"
              >
                Start HITL
              </button>
            </div>
          </div>
          <div
            id="micro-hitl-auth-panel"
            v-show="authSectionExpanded"
            class="micro-hitl-jwt-start__panel"
          >
            <p class="micro-hitl-jwt-start__help">
              Paste a JWT if the session check above did not find a valid
              <code class="micro-hitl-stage__code">or</code>
              cookie. The widget calls the shepherd-vet auth API with
              <code class="micro-hitl-stage__code">POST { token }</code>
              , then sets the
              <code class="micro-hitl-stage__code">or</code>
              cookie from
              <code class="micro-hitl-stage__code">payload</code>
              and
              <code class="micro-hitl-stage__code">or_expire</code>
              from
              <code class="micro-hitl-stage__code">ttl</code>
              (or skips that when the session cookie already passes validation).
            </p>
            <label class="micro-hitl-jwt-start__field">
              <span class="micro-hitl-jwt-start__label">JWT</span>
              <textarea
                v-model="jwtInput"
                class="micro-hitl-jwt-start__input"
                rows="3"
                autocomplete="off"
                spellcheck="false"
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9…"
                :disabled="microHitlEmbedStarted"
              />
            </label>
            <p class="micro-hitl-jwt-start__logout-hint">
              <strong>Log out</strong>
              revokes the OneReach session token and clears the
              <code class="micro-hitl-stage__code">or</code>
              /
              <code class="micro-hitl-stage__code">or_expire</code>
              cookies (the embed stops if it was running).
            </p>
          </div>
        </section>

        <section class="micro-demo-cards" aria-label="Demo contacts">
          <div class="micro-demo-cards__toolbar">
            <button
              type="button"
              class="micro-demo-cards__toggle"
              :aria-expanded="contactsSectionExpanded"
              aria-controls="micro-hitl-contacts-panel"
              @click="contactsSectionExpanded = !contactsSectionExpanded"
            >
              <span
                class="micro-demo-cards__chevron"
                :class="{ 'micro-demo-cards__chevron--open': contactsSectionExpanded }"
                aria-hidden="true"
              />
              <span class="micro-demo-cards__toolbar-title">Mock CRM contacts</span>
            </button>
            <p class="micro-demo-cards__toolbar-status" role="status">
              {{ contactsSectionStatusLine }}
            </p>
          </div>
          <div
            id="micro-hitl-contacts-panel"
            v-show="contactsSectionExpanded"
            class="micro-demo-cards__panel"
          >
          <label class="micro-demo-cards__sms-prefill">
            <span class="micro-demo-cards__sms-label">Initial SMS message (optional)</span>
            <textarea
              v-model="smsInitialMessage"
              class="micro-demo-cards__sms-input"
              rows="2"
              maxlength="2000"
              placeholder="e.g. Hi, following up on your request…"
            />
          </label>
          <ul class="micro-demo-cards__list">
            <li
              v-for="c in mockContacts"
              :key="c.id"
              class="micro-demo-card"
            >
              <div class="micro-demo-card__main">
                <span class="micro-demo-card__name">{{ c.name }}</span>
                <span class="micro-demo-card__meta">{{ c.detail }}</span>
                <span class="micro-demo-card__id">id: {{ c.id }}</span>
              </div>
              <div class="micro-demo-card__actions">
                <button
                  type="button"
                  class="micro-demo-card__btn micro-demo-card__btn--primary"
                  :disabled="!microHitlReady || !microHitlEmbedStarted"
                  @click="microSelectContact(c.id)"
                >
                  Open in HITL
                </button>
                <button
                  type="button"
                  class="micro-demo-card__btn"
                  :disabled="!microHitlReady || !microHitlEmbedStarted"
                  @click="microStartConversation(c.id, 'text', c.phone)"
                >
                  New SMS
                </button>
                <button
                  type="button"
                  class="micro-demo-card__btn"
                  :disabled="!microHitlReady || !microHitlEmbedStarted"
                  @click="microStartConversation(c.id, 'text')"
                >
                  New SMS (no identifier)
                </button>
                <button
                  type="button"
                  class="micro-demo-card__btn"
                  :disabled="!microHitlReady || !microHitlEmbedStarted"
                  @click="microStartConversation(c.id, 'voice', c.phone)"
                >
                  Voice
                </button>
              </div>
            </li>
          </ul>
          </div>
        </section>
      </div>
      <div class="agent-ui-wrapper agent-ui-wrapper--embedded agent-ui-wrapper--micro">
        <MicroHitlWidget
          v-if="microHitlEmbedStarted"
          :jwt-token="committedJwt"
          @ready="onMicroHitlReady"
          @hitl-event="onMicroHitlHostEvent"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { MOCK_CONTACTS } from '@/constants/env.js';
import MicroHitlWidget from './MicroHitlWidget.vue';
import { isOrCookieTokenAllowed, logoutOrCookieSession } from '@/utils/orCookieSession.js';

export default {
  name: 'MicroHitlDemo',
  components: { MicroHitlWidget },
  data() {
    return {
      callbackId: 'mini-hitl-embed-demo',
      /** Collapsed by default; expand to paste a JWT. */
      authSectionExpanded: false,
      /** Collapsed by default; expand to use mock CRM contacts. */
      contactsSectionExpanded: false,
      orLogoutInProgress: false,
      /** `checking` until mounted probe finishes — same logic as MicroHitlWidget. */
      orTokenCheckState: 'checking',
      jwtInput: '',
      /** Snapshot passed to the widget when the user clicks Start HITL. */
      committedJwt: '',
      microHitlEmbedStarted: false,
      microHitlApi: null,
      /** Set when micro-bus `APP_READY` is received; gates contact actions until the app is live. */
      microHitlReady: false,
      smsInitialMessage: '',
      mockContacts: MOCK_CONTACTS,
    };
  },
  computed: {
    jwtInputTrimmed() {
      return (this.jwtInput || '').trim();
    },
    orTokenStatusLine() {
      switch (this.orTokenCheckState) {
        case 'checking':
          return 'Checking or session cookie…';
        case 'allowed':
          return 'Valid or session — JWT optional.';
        default:
          return 'No active or session — expand to paste a JWT.';
      }
    },
    canStartMicroHitl() {
      if (this.microHitlEmbedStarted) return false;
      if (this.orTokenCheckState === 'checking') return false;
      if (this.orTokenCheckState === 'allowed') return true;
      return this.jwtInputTrimmed.length > 0;
    },
    contactsSectionStatusLine() {
      if (!this.microHitlEmbedStarted) {
        if (this.orTokenCheckState === 'checking') {
          return 'Checking session — start HITL to use contacts.';
        }
        if (this.orTokenCheckState === 'allowed') {
          return 'Start HITL to interact with contacts.';
        }
        return 'Authenticate and start HITL first.';
      }
      if (!this.microHitlReady) {
        return 'Waiting for APP_READY…';
      }
      return `${this.mockContacts.length} contacts — ready.`;
    },
  },
  mounted() {
    this.probeOrCookieSession();
  },
  methods: {
    async probeOrCookieSession() {
      this.orTokenCheckState = 'checking';
      const ok = await isOrCookieTokenAllowed();
      this.orTokenCheckState = ok ? 'allowed' : 'denied';
    },

    startMicroHitlEmbed() {
      if (!this.canStartMicroHitl) return;
      this.committedJwt = this.jwtInputTrimmed;
      this.microHitlEmbedStarted = true;
    },

    async logoutOrSession() {
      if (this.orLogoutInProgress) return;
      this.orLogoutInProgress = true;
      try {
        await logoutOrCookieSession();
        this.microHitlEmbedStarted = false;
        this.microHitlReady = false;
        this.microHitlApi = null;
        await this.probeOrCookieSession();
      } finally {
        this.orLogoutInProgress = false;
      }
    },

    onMicroHitlReady(api) {
      this.microHitlApi = api;
      this.microHitlReady = false;
    },

    onMicroHitlHostEvent(payload) {
      const { type, content } = payload || {};
      
      if (type === 'APP_READY') {
        this.microHitlReady = true;
      }

      console.log('[mini-HITL → host]', type, content);
    },

    openMicroHitlPopup() {
      const openPopup = this.microHitlApi?.openPopup;
      if (!openPopup) return;
  
      openPopup();
    },

    microSelectContact(contactId) {
      this.openMicroHitlPopup();

      const emit = this.microHitlApi?.emit;

      emit({
        message: 'HITL::select-contact',
        params: {
          callbackId: this.callbackId,
          params: { contactId },
        },
      });

      this.microHitlApi?.navigate('timeline');
    },

    async microStartConversation(contactId, type, phone) {
      this.openMicroHitlPopup();
      
      const emit = this.microHitlApi?.emit;
      const message = (this.smsInitialMessage || '').trim();
      const params = {
        contactId,
        type,
        params: {
          ...(message ? { message } : {}),
          ...(phone ? { identifier: phone } : {}),
        },
      };
      
      emit({ message: 'HITL::start-conversation', params });
    },
  },
};
</script>

<style scoped lang="scss">
.micro-hitl-demo {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
  background: linear-gradient(180deg, #e8ecf4 0%, #f2f5f9 28%, #fafbfc 100%);
}

.embed-purpose {
  flex-shrink: 0;
  padding: 28px 24px 16px;
  max-width: 960px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;

  &__title {
    margin: 0 0 14px;
    font-size: clamp(1.45rem, 2.6vw, 1.85rem);
    font-weight: 700;
    color: #141823;
    letter-spacing: -0.03em;
    line-height: 1.22;
  }

  &__text {
    margin: 0;
    font-size: 15px;
    line-height: 1.6;
    color: #4a5568;
    max-width: 52rem;
  }
}

.micro-hitl-stage {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  position: relative;
  padding: 0 24px 36px;
  max-width: 960px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;

  &__mock-page {
    flex: 0 0 auto;
    min-height: 120px;
    margin: 12px 0;
    padding: 18px 22px;
    border: 1px solid #cfd8e6;
    border-radius: 10px;
    box-shadow:
      0 1px 2px rgba(15, 23, 42, 0.05),
      0 4px 14px rgba(15, 23, 42, 0.06);
    background: linear-gradient(180deg, #f8fafc 0%, #fff 46%);
    color: #555;
    font-size: 14px;
    line-height: 1.45;
  }

  &__mock-header {
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
    font-size: 15px;
  }

  &__mock-body {
    margin: 0 0 16px;
  }

  &__code {
    font-size: 12px;
    padding: 1px 6px;
    border-radius: 4px;
    background: #eef2f7;
    color: #1a3a5c;
  }
}

.micro-hitl-jwt-start {
  margin: 0 0 20px;
  padding: 14px 16px;
  border: 1px solid #c5d4e8;
  border-radius: 10px;
  background: #f0f6fc;
  max-width: 42rem;

  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 14px;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 4px 8px 4px 4px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font: inherit;
    color: #1a3a5c;
    cursor: pointer;

    &:hover {
      background: rgba(25, 118, 210, 0.08);
    }

    &:focus-visible {
      outline: 2px solid #1976d2;
      outline-offset: 2px;
    }
  }

  &__chevron {
    display: block;
    width: 0.4em;
    height: 0.4em;
    border-right: 2px solid #334155;
    border-bottom: 2px solid #334155;
    transform: rotate(-45deg);
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(45deg);
    }
  }

  &__toolbar-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  &__toolbar-status {
    flex: 1 1 200px;
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
    color: #475569;
    min-width: 0;
  }

  &__toolbar-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: auto;
  }

  &__logout-hint {
    margin: 12px 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: #64748b;
  }

  &__panel {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #c5d4e8;
  }

  &__help {
    margin: 0 0 12px;
    font-size: 13px;
    line-height: 1.5;
    color: #4a5568;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-size: 13px;
    font-weight: 500;
    color: #444;
  }

  &__input {
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid #a8b8d0;
    border-radius: 6px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    line-height: 1.4;
    resize: vertical;
    min-height: 4rem;

    &::placeholder {
      color: #94a3b8;
    }

    &:disabled {
      background: #e8eef5;
      color: #64748b;
    }
  }

  &__btn {
    padding: 8px 16px;
    border: 1px solid #c5c5c5;
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #f5f5f5;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--primary {
      border-color: #1976d2;
      background: #1976d2;
      color: #fff;

      &:hover:not(:disabled) {
        background: #1565c0;
        border-color: #1565c0;
      }
    }
  }
}

.micro-demo-cards {
  margin-top: 8px;
  padding: 14px 16px;
  border: 1px solid #c5d4e8;
  border-radius: 10px;
  background: #f0f6fc;
  max-width: 42rem;

  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 14px;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 4px 8px 4px 4px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font: inherit;
    color: #1a3a5c;
    cursor: pointer;

    &:hover {
      background: rgba(25, 118, 210, 0.08);
    }

    &:focus-visible {
      outline: 2px solid #1976d2;
      outline-offset: 2px;
    }
  }

  &__chevron {
    display: block;
    width: 0.4em;
    height: 0.4em;
    border-right: 2px solid #334155;
    border-bottom: 2px solid #334155;
    transform: rotate(-45deg);
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(45deg);
    }
  }

  &__toolbar-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  &__toolbar-status {
    flex: 1 1 200px;
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
    color: #475569;
    min-width: 0;
  }

  &__panel {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #c5d4e8;
  }

  &__sms-prefill {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0 0 14px;
    max-width: 42rem;
  }

  &__sms-label {
    font-size: 13px;
    font-weight: 500;
    color: #444;
  }

  &__sms-input {
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid #c5c5c5;
    border-radius: 6px;
    font: inherit;
    font-size: 13px;
    line-height: 1.4;
    resize: vertical;
    min-height: 2.75rem;

    &::placeholder {
      color: #888;
    }
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__hint {
    margin: 12px 0 0;
    font-size: 13px;
    color: #666;
  }

  &__last {
    margin: 12px 0 0;
    font-size: 13px;
    line-height: 1.45;
    color: #333;
    word-break: break-word;
  }

  &__last-params {
    display: block;
    margin-top: 4px;
    font-family: ui-monospace, monospace;
    font-size: 12px;
    color: #555;
  }
}

.micro-demo-card {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #d8e0ea;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  &__main {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    flex: 1 1 200px;
  }

  &__name {
    font-weight: 600;
    font-size: 15px;
    color: #1a1a1a;
  }

  &__meta {
    font-size: 13px;
    color: #555;
  }

  &__id {
    font-size: 11px;
    font-family: ui-monospace, monospace;
    color: #888;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    flex: 0 1 auto;
  }

  &__btn {
    padding: 6px 12px;
    border: 1px solid #c5c5c5;
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #f5f5f5;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--primary {
      border-color: #1976d2;
      color: #1976d2;
      font-weight: 600;

      &:hover:not(:disabled) {
        background: #e3f2fd;
      }
    }
  }
}

.agent-ui-wrapper--micro {
  flex: 1;
  min-height: 280px;
}

.agent-ui-wrapper {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  min-height: 0;

  &--embedded {
    position: absolute;
  }
}
</style>
